import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

interface ModalContextType {
  openSessionId: string | null;
  openModals: Set<string>;
  setOpenSessionId: (sessionId: string | null) => void;
  registerModal: (modalId: string) => void;
  unregisterModal: (modalId: string) => void;
  hasOpenModals: () => boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [openSessionId, setOpenSessionIdState] = useState<string | null>(null);
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());
  const openModalsRef = useRef<Set<string>>(new Set());

  const setOpenSessionId = useCallback((sessionId: string | null) => {
    // Só permite fechar o dropdown se não houver modais abertos
    if (sessionId === null && openModalsRef.current.size > 0) {
      return; // Não fecha se houver modais abertos
    }
    setOpenSessionIdState(sessionId);
  }, []);

  const registerModal = useCallback((modalId: string) => {
    setOpenModals((prev) => {
      const newModals = new Set(prev);
      newModals.add(modalId);
      openModalsRef.current = newModals;
      return newModals;
    });
  }, []);

  const unregisterModal = useCallback((modalId: string) => {
    setOpenModals((prev) => {
      const newModals = new Set(prev);
      newModals.delete(modalId);
      openModalsRef.current = newModals;
      return newModals;
    });
  }, []);

  const hasOpenModals = useCallback(() => {
    return openModalsRef.current.size > 0;
  }, []);

  return (
    <ModalContext.Provider
      value={{
        openSessionId,
        openModals,
        setOpenSessionId,
        registerModal,
        unregisterModal,
        hasOpenModals,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function useModalStore() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalStore must be used within a ModalProvider');
  }
  return context;
}

